import React, { useEffect, useState } from 'react'

import { Col, Container, Row } from 'react-bootstrap'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { ExampleBox } from '../../../../../components/ExampleBox/ExampleBox'
import { Button } from '../../../../../components/Button/Button'
import Plus from '../../../../../assets/images/plus-icon.svg?url'
import PlusDark from '../../../../../assets/images/plus-dark.svg?url'
import { ReactComponent as Sparkling } from '../../../../../assets/images/sparkling.svg'
import styled from '@emotion/styled'
import { TemplateInputParameters } from '../../../../../models/AiFeature'

type GeneratingBoxProps = {
  errorMessage: string | null
}

const GeneratingBox = styled.div<GeneratingBoxProps>`
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid ${(props) => (props.errorMessage ? 'var(--zocks-danger)' : '#212529')};
  box-sizing: border-box;
`

export interface GeneratingFormViewModalProps {
  inputParameters: TemplateInputParameters
  onChangeExample: (field: string, newValue: string) => void
  generateTemplate: () => Promise<string | undefined>
  generateIsLoading: boolean
  generatingEnabled: boolean
  errorMessage: string | null
  shouldReset: boolean
}

export const GeneratingForm: React.FC<GeneratingFormViewModalProps> = ({
  inputParameters,
  onChangeExample,
  generateTemplate,
  generateIsLoading,
  generatingEnabled,
  errorMessage,
  shouldReset,
}) => {
  const { example_1, example_2 } = inputParameters

  const [multipleExamples, setMultipleExamples] = useState<boolean>(!!example_1 && !!example_2)

  const [openExample1, setOpenExample1] = useState(true)
  const [openExample2, setOpenExample2] = useState(true)

  useEffect(() => {
    if (generateIsLoading) {
      setOpenExample1(false)
      setOpenExample2(false)
    }
  }, [generateIsLoading])

  return (
    <div className='pb-3'>
      <Container className='d-flex flex-column gap-4 p-0'>
        <div className='zocks-mb-1'>
          By providing your examples, we will generate a personalized template for you. This template will be used to create results after your meetings.
        </div>
        <div>
          <OverlayTrigger
            placement='right'
            overlay={
              <Tooltip className='template-tooltip'>
                Please provide one or two samples that we can use to generate a universal template for your future meetings.
              </Tooltip>
            }
          >
            {({ ref, ...triggerHandler }) => (
              <div className='d-flex align-items-center gap-2 mb-1'>
                <div className='paragraph-medium'>Generate template</div>
                <i {...triggerHandler} ref={ref} className='bi bi-info-circle' />
              </div>
            )}
          </OverlayTrigger>
          <GeneratingBox errorMessage={errorMessage}>
            <div className='lead-small mb-2'>Add example</div>
            <Col>
              <ExampleBox
                example={example_1 ?? ''}
                onChange={(newExample) => onChangeExample('example_1', newExample)}
                isOpen={openExample1}
                toggleOpen={() => setOpenExample1((v) => !v)}
                shouldReset={shouldReset}
              />
            </Col>
            {multipleExamples && (
              <Col>
                <ExampleBox
                  example={example_2 ?? ''}
                  onChange={(newExample) => onChangeExample('example_2', newExample)}
                  onDelete={() => {
                    onChangeExample('example_2', '')
                    setMultipleExamples(false)
                  }}
                  isOpen={openExample2}
                  toggleOpen={() => setOpenExample2((v) => !v)}
                  shouldReset={shouldReset}
                />
              </Col>
            )}
            <Col>
              <Button className='button-small' onClick={() => setMultipleExamples(true)} variant='link' disabled={multipleExamples}>
                <img alt='Add' src={multipleExamples ? Plus : PlusDark} />
                Add
              </Button>
            </Col>
            <Row className='justify-content-end me-1'>
              <Button className='button-small zocks-mb-1' onClick={generateTemplate} variant='dark' disabled={!generatingEnabled}>
                <Sparkling className='zocks-text-white' />
                Generate template
              </Button>
              {errorMessage ? <div className='paragraph-medium zocks-text-danger'>{errorMessage}</div> : null}
            </Row>
          </GeneratingBox>
        </div>
      </Container>
    </div>
  )
}
